import { initSentry } from "./instrument";
import { StrictMode, useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import * as Sentry from "@sentry/react";

import "./styles.css";

import { AuthUIProvider } from "@daveyplate/better-auth-ui";

import { ThemeProvider } from "./lib/theme-context";
import { AuthProvider } from "./lib/auth-context";
import { ProProvider } from "./lib/pro-context";
import { queryClient } from "./lib/query-client";
import { authClient } from "./lib/auth-client";
import { routeTree } from "./routes";

function ResponsiveToaster() {
	const [position, setPosition] = useState("top-right");

	useEffect(() => {
		const mql = window.matchMedia("(max-width: 639px)");
		const onChange = (e) => setPosition(e.matches ? "top-center" : "top-right");
		mql.addEventListener("change", onChange);
		setPosition(mql.matches ? "top-center" : "top-right");
		return () => mql.removeEventListener("change", onChange);
	}, []);

	return (
		<Toaster
			position={position}
			toastOptions={{
				duration: 4000,
				style: {
					background: "var(--foreground-default)",
					color: "var(--foreground-inverse)",
				},
				success: {
					duration: 3000,
					iconTheme: {
						primary: "var(--success)",
						secondary: "var(--foreground-inverse)",
					},
				},
				error: {
					duration: 5000,
					iconTheme: {
						primary: "var(--error)",
						secondary: "var(--foreground-inverse)",
					},
				},
			}}
		/>
	);
}

const router = createRouter({
	routeTree,
	context: { queryClient },
	defaultPreload: "intent",
	scrollRestoration: true,
	defaultStructuralSharing: true,
	defaultPreloadStaleTime: 0,
	defaultPendingMs: 500,
	defaultPendingMinMs: 200,
});

initSentry(router);

// Register service worker (skip in Vite dev server — HMR conflicts with SW caching)
if ("serviceWorker" in navigator && !import.meta.env.DEV) {
	window.addEventListener("load", () => {
		navigator.serviceWorker
			.register("/sw.js", { scope: "/" })
			.then((registration) => {
				// Check for updates every 30 minutes
				setInterval(() => registration.update(), 30 * 60 * 1000);

				// When a new SW is waiting, activate it immediately
				registration.addEventListener("updatefound", () => {
					const newWorker = registration.installing;
					if (!newWorker) return;

					newWorker.addEventListener("statechange", () => {
						if (
							newWorker.state === "installed" &&
							navigator.serviceWorker.controller
						) {
							// New version available — activate it
							newWorker.postMessage("SKIP_WAITING");
						}
					});
				});
			})
			.catch((err) => console.warn("SW registration failed:", err));
	});

	// Reload when new SW takes over (ensures fresh assets)
	navigator.serviceWorker.addEventListener("controllerchange", () => {
		window.location.reload();
	});
}

const rootElement = document.getElementById("app");
if (rootElement && !rootElement.innerHTML) {
	const root = ReactDOM.createRoot(rootElement, {
		onUncaughtError: Sentry.reactErrorHandler(),
		onCaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
			console.warn("Caught error:", error, errorInfo.componentStack);
		}),
	});
	root.render(
		<StrictMode>
			<QueryClientProvider client={queryClient}>
				<AuthUIProvider
					authClient={authClient}
					navigate={({ href }) => {
						router.navigate({ to: href });
					}}
					replace={({ href }) => {
						router.navigate({ to: href, replace: true });
					}}
					social={{
						providers: ["google", "facebook"],
					}}
					credentials={{
						passwordValidation: {
							minLength: 8,
						},
						confirmPassword: false,
					}}
					redirectTo="/"
					onSessionChange={() => {
						queryClient.clear();
					}}
				>
					<ThemeProvider>
						<AuthProvider>
							<ProProvider>
								<RouterProvider router={router} />
							</ProProvider>
						</AuthProvider>
					</ThemeProvider>
				</AuthUIProvider>
				<ResponsiveToaster />
				</QueryClientProvider>
		</StrictMode>,
	);
}
